import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { Mail, Phone, MessageCircle, Zap, Key, Terminal, Clock, Users, Code2, Shield, Database, Send, ArrowRight, Star, CheckCircle } from 'lucide-react';

const Contact = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    apiType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simular envío
    setTimeout(() => {
      alert('¡Solicitud de API enviada! Te contactaremos en menos de 2 horas con documentación y keys de prueba.');
      setFormData({ name: '', email: '', company: '', apiType: '', message: '' });
      setIsSubmitting(false);
    }, 2000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Soporte Técnico',
      value: 'dev@visioner7.com',
      description: 'Respuesta en menos de 2 horas',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Phone,
      label: 'Consultas APIs',
      value: '+51 955-000321',
      description: 'Lun a Vie 9:00 - 18:00',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: MessageCircle,
      label: 'Chat Developers',
      value: 'Discord & Slack',
      description: 'Comunidad técnica activa',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const apiTypes = [
    'Consulta RUC/DNI',
    'Facturación Electrónica',
    'Notas Crédito/Débito',
    'Buzón Electrónico',
    'Tipo de Cambio',
    'Descarga Archivos',
    'Multiple/Integración Completa'
  ];

  const features = [
    {
      icon: Zap,
      title: 'Sandbox Ilimitado',
      description: 'Pruebas sin restricciones'
    },
    {
      icon: Terminal,
      title: 'SDKs Oficiales',
      description: 'Python, JS, Java, PHP, C#'
    },
    {
      icon: Key,
      title: 'API Keys',
      description: 'Credenciales seguras'
    },
    {
      icon: MessageCircle,
      title: 'Soporte 24/7',
      description: 'Asistencia técnica'
    },
    {
      icon: Database,
      title: 'Documentación',
      description: 'Guías completas'
    },
    {
      icon: Shield,
      title: 'Seguridad',
      description: 'TLS 1.3, OAuth 2.0'
    }
  ];

  const testimonials = [
    {
      name: "Carlos Rodríguez",
      company: "Tech Solutions SAC",
      comment: "Implementamos en 3 días. El soporte técnico es excepcional.",
      rating: 5
    },
    {
      name: "María González",
      company: "Contabilidad Plus",
      comment: "Las APIs son muy estables y la documentación es clara.",
      rating: 5
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo mejorados */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:80px_80px]" />
        </div>
        
        {/* Partículas animadas */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-blue-500/10 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 6,
            }}
          />
        ))}

        {/* Formas geométricas */}
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-cyan-500/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado mejorado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-sm border border-blue-200 rounded-2xl px-6 py-3 mb-8 shadow-lg"
          >
            <Key className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-mono text-blue-700 font-medium">
              SANDBOX ACCESS • API KEYS • TECHNICAL SUPPORT
            </span>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Comienza con{' '}
            <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-800 bg-clip-text text-transparent">
              Nuestras APIs
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light mb-8">
            Obtén acceso inmediato a nuestras APIs SUNAT. Documentación técnica completa, 
            SDKs preparados y soporte especializado para tu integración.
          </p>

          {/* Stats rápidas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-8 mt-12"
          >
            {[
              { icon: Users, value: '500+', label: 'Clientes Activos' },
              { icon: CheckCircle, value: '99.9%', label: 'Uptime' },
              { icon: Clock, value: '<2h', label: 'Respuesta' },
              { icon: Code2, value: '15+', label: 'SDKs' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Sección izquierda - Información mejorada */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Tarjeta de contacto */}
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-8 shadow-xl">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Soporte Especializado</h3>
              <p className="text-gray-600 text-lg mb-8">Equipo técnico experto en integraciones SUNAT</p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <motion.div
                      key={info.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.15 }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4 p-5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border border-blue-100 hover:border-blue-300 transition-all duration-300 group cursor-pointer hover:shadow-lg"
                      whileHover={{ x: 6, scale: 1.02 }}
                    >
                      <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-lg mb-1">{info.label}</p>
                        <p className="text-gray-700 text-base font-medium mb-1">{info.value}</p>
                        <p className="text-gray-500 text-sm">{info.description}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transform group-hover:translate-x-1 transition-all" />
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Características */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-3xl p-8 text-white shadow-2xl"
            >
              <h4 className="text-2xl font-bold mb-6 flex items-center">
                <Zap className="w-6 h-6 mr-3" />
                Incluido con tu prueba
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center space-x-3 group"
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{feature.title}</div>
                        <div className="text-blue-100 text-xs">{feature.description}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Testimonios */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 shadow-lg"
            >
              <h4 className="text-xl font-bold text-gray-900 mb-4">Lo que dicen nuestros clientes</h4>
              <div className="space-y-4">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + index * 0.2 }}
                    viewport={{ once: true }}
                    className="p-4 bg-gray-50 rounded-2xl"
                  >
                    <div className="flex items-center mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-600 text-sm mb-2">"{testimonial.comment}"</p>
                    <div className="text-xs text-gray-500">
                      <strong>{testimonial.name}</strong> - {testimonial.company}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Sección derecha - Formulario mejorado */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-8 shadow-2xl hover:shadow-2xl transition-all duration-500">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Solicita tu Acceso</h3>
                <p className="text-gray-600">Completa el formulario y recibe tus credenciales en minutos</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Nombre / Empresa *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ej: Tech Solutions SAC"
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base bg-white/50 backdrop-blur-sm"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@empresa.com"
                      className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base bg-white/50 backdrop-blur-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Razón Social"
                      className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base bg-white/50 backdrop-blur-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    ¿Qué API necesitas? *
                  </label>
                  <select
                    name="apiType"
                    value={formData.apiType}
                    onChange={handleChange}
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base bg-white/50 backdrop-blur-sm"
                    required
                  >
                    <option value="">Selecciona una API</option>
                    {apiTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Caso de Uso Específico *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Ej: Necesito integrar facturación electrónica en mi ERP con 1000 transacciones mensuales, requiero manejo de notas de crédito y consulta masiva de RUC..."
                    rows="5"
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-base bg-white/50 backdrop-blur-sm"
                    required
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                    />
                  ) : (
                    <>
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      <span>Solicitar Acceso a Sandbox</span>
                    </>
                  )}
                </motion.button>

                <p className="text-center text-gray-500 text-sm">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Te enviaremos las credenciales en menos de 2 horas • Sin compromiso
                </p>
              </div>
            </form>

            {/* Información adicional mejorada */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl p-6 text-white shadow-2xl"
            >
              <div className="flex items-start space-x-4">
                <Zap className="w-8 h-8 text-white flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-lg mb-2">¿Necesitas integración urgente?</h4>
                  <p className="text-blue-100 text-sm mb-3">
                    Nuestro equipo técnico está listo para ayudarte inmediatamente
                  </p>
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="w-4 h-4" />
                    <span className="font-bold">+51 955-000-321</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;